/**
 * PlanDefinitionMappingPanel - right rail (Sprint 3.4 REDO).
 *
 * Renders the static "Maps to" arrow and the canonical TLP1 mapping
 * card per the prototype right rail. The card lists the protocol
 * components the publish action will compose into a FHIR
 * `PlanDefinition`:
 *   - Protocol Steps (4 ActivityDefinitions)
 *   - Medications (RXNORM codes)
 *   - Lab Orders (LOINC codes)
 *   - PROMs schedule
 *   - Scheduling Rules
 *   - Exclusion Criteria
 *
 * Numbers come from `TLP1_MAPPING_SUMMARY` in `./types.ts` so a future
 * pathway swap is a one-line edit.
 *
 * HC2 token-only.
 * HC5 zero em / en dashes.
 */
import {
  TLP1_MAPPING_SUMMARY,
  type PlanDefinitionMappingSummary,
} from './types';

export interface PlanDefinitionMappingPanelProps {
  summary?: PlanDefinitionMappingSummary;
}

interface MappingRow {
  label: string;
  detail: string;
}

function buildRows(summary: PlanDefinitionMappingSummary): MappingRow[] {
  return [
    {
      label: 'Protocol Steps',
      detail: `${summary.protocolStepCount} ActivityDefinitions`,
    },
    {
      label: 'Medications',
      detail: `${summary.medicationRxnormCount} RXNORM codes`,
    },
    {
      label: 'Lab Orders',
      detail: `${summary.labLoincCount} LOINC codes`,
    },
    {
      label: 'PROMs schedule',
      detail: `${summary.promInstrumentCount} instruments`,
    },
    {
      label: 'Scheduling Rules',
      detail: `${summary.schedulingRulesCount} rules`,
    },
    {
      label: 'Exclusion Criteria',
      detail: `${summary.exclusionCriteriaCount} criteria`,
    },
  ];
}

export function PlanDefinitionMappingPanel({
  summary = TLP1_MAPPING_SUMMARY,
}: PlanDefinitionMappingPanelProps) {
  const rows = buildRows(summary);
  return (
    <aside
      data-testid="plan-definition-mapping-panel"
      aria-labelledby="plan-definition-mapping-title"
      className="flex flex-col gap-gutter-md"
    >
      <div
        aria-hidden="true"
        data-testid="plan-definition-mapping-arrow"
        className="flex items-center gap-gutter-sm text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary"
      >
        <span className="h-px flex-1 bg-surface-border" />
        Maps to
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="currentColor"
          aria-hidden="true"
        >
          <polygon points="0,2 8,6 0,10" />
        </svg>
      </div>
      <article
        className="rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card border-l-4 border-l-signal-primary-500"
      >
        <header>
          <p className="text-signal-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Target
          </p>
          <h3
            id="plan-definition-mapping-title"
            className="mt-gutter-sm text-signal-lg font-semibold text-text-primary"
          >
            Scrips Cardiometabolic Pathway FHIR PlanDefinition
          </h3>
          <p
            data-testid="plan-definition-mapping-pathway-id"
            className="mt-gutter-sm text-signal-xs text-text-tertiary"
          >
            {summary.pathwayId}
          </p>
        </header>
        <ul
          role="list"
          aria-label="PlanDefinition components"
          className="mt-gutter-md space-y-gutter-sm"
        >
          {rows.map((row) => (
            <li
              key={row.label}
              data-testid={`plan-definition-mapping-row-${row.label
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
              className="flex items-center justify-between gap-gutter-md text-signal-sm"
            >
              <span className="text-text-secondary">{row.label}</span>
              <span className="font-semibold text-text-primary">
                {row.detail}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-gutter-md text-signal-xs text-text-tertiary">
          The publish action serialises the current selection and posts a
          FHIR `PlanDefinition` to the live FHIR client.
        </p>
      </article>
    </aside>
  );
}
